import type { Metadata } from "@/lib/head";
import { Link, useRouter } from "@/lib/nav";
import { ChevronLeft } from "lucide-react";
import { useState, type FormEvent } from "react";

import CustomerScreenShell from "@/components/customer-screen-shell";
import Pill from "@buildpy/ui/pill";
import SectionCard from "@buildpy/ui/section-card";
import { cn } from "@buildpy/ui/cn";
import { Button } from "@buildpy/ui/button";
import { Input } from "@buildpy/ui/input";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@buildpy/ui/sheet";
import { formatDate, formatDateTime } from "@buildpy/core/lib/format";
import { customerProjectPath } from "@buildpy/core/modules/project/customer-visible";
import { PROJECT_LOG_KIND_LABEL } from "@buildpy/core/modules/project/types";
import type { CustomerLogComment, CustomerLogPhoto, CustomerProjectLogView } from "@buildpy/core/views/g3b/customer-project-log";
import { addLogCommentByLink } from "@/api/actions/project-log-share";
import { verifyContractShare } from "@/api/actions/contract-share";
import { settleRedirect } from "@/lib/redirect-error";
import UnusableLink from "@/modules/contract/ui/unusable-link";
import { APP_SHEET_KEY, browserSeenStore, markSeen, shouldInvite } from "@/modules/project/ui/app-invite";
import CustomerPhotoThumb from "@/modules/project/ui/customer-photo-thumb";
import { LOG_KIND_TONE } from "@/modules/project/ui/log-kind-tone";
import ShareGate from "@/modules/share/ui/share-gate";

export const metadata: Metadata = {
  title: "시공일지",
  robots: { index: false, follow: false },
};

interface Props {
  token: string;
  data: CustomerProjectLogView;
}

const FAILED = "댓글을 남기지 못했습니다. 잠시 뒤 다시 남겨 주세요";

// 서버 검사와 같은 코드포인트 기준으로 센다.
const lengthOf = (value: string): number => Array.from(value).length;

// 댓글 쓴 사람 — 고객 글은 「나」, 이름 없는 직원 글(나간 직원)은 시공사 이름.
const commentAuthorOf = (c: Pick<CustomerLogComment, "author_kind" | "author_name">, orgName: string): string =>
  c.author_kind === "CUSTOMER" ? "나" : c.author_name ?? orgName;

// 사진 바이트는 api 가 입장 쿠키로 내준다 — 화면 주소(/c/…) 앞에 /api 를 붙인다(「내 현장」 과 같다).
const LogPhotos = ({ photos }: { photos: readonly CustomerLogPhoto[] }) => (
  <ul className="grid grid-cols-2 gap-2" data-testid="customer-log-photos">
    {photos.map((p, i) => (
      <li key={p.id}>
        <figure className="flex flex-col gap-y-1">
          <CustomerPhotoThumb alt={p.caption || p.phase_name || `현장 사진 ${i + 1}`} src={`/api${p.path}`} />
          {p.caption && <figcaption className="text-sm text-muted-foreground">{p.caption}</figcaption>}
        </figure>
      </li>
    ))}
  </ul>
);

const Notice = ({ text }: { text: string }) => (
  <CustomerScreenShell>
    <SectionCard as="div" className="mx-auto w-full max-w-sm p-5 text-center text-sm" padding="none" role="alert">
      {text}
    </SectionCard>
  </CustomerScreenShell>
);

const Header = ({ token, title }: { token: string; title: string }) => (
  <header className="pt-safe sticky top-0 z-10 border-b border-border bg-card">
    <div className="flex items-center gap-x-1 px-2 py-1.5">
      <Button asChild aria-label="공사 첫 화면으로" size="icon" variant="ghost">
        <Link data-testid="customer-log-back" href={customerProjectPath(token)}>
          <ChevronLeft />
        </Link>
      </Button>
      <h1 className="text-base font-semibold">{title}</h1>
    </div>
  </header>
);

// 첫 댓글 뒤 한 번 — 앱으로 받으면 답글 알림이 온다는 안내 · 스토어 주소가 아직 없어 두 버튼 모두 닫기만 한다.
const AppSheet = ({ open, onClose }: { open: boolean; onClose: () => void }) => (
  <Sheet open={open} onOpenChange={(next) => !next && onClose()}>
    <SheetContent className="mx-auto flex max-w-md flex-col gap-y-4 p-5" data-testid="customer-app-sheet" side="bottom">
      <SheetHeader className="flex-col items-start gap-y-1 p-0">
        <SheetTitle>댓글을 남겼어요</SheetTitle>
        <SheetDescription>담당자가 답하면 바로 알려드릴까요</SheetDescription>
      </SheetHeader>
      <ul className="flex list-disc flex-col gap-y-1 pl-5 text-sm">
        <li>답글이 달리면 알림으로 먼저 와요</li>
        <li>새 시공일지와 사진도 바로 받아요</li>
        <li>링크를 찾지 않아도 앱에서 바로 열려요</li>
      </ul>
      {/* 마지막 줄 아래로 안전 영역(홈 표시줄)만큼 더 띄운다. */}
      <div className="flex flex-col gap-y-2 pb-safe">
        <Button size="lg" variant="primary" onClick={onClose}>
          앱으로 알림 받기
        </Button>
        <Button size="lg" variant="ghost-muted" onClick={onClose}>
          지금은 링크로 볼게요
        </Button>
      </div>
    </SheetContent>
  </Sheet>
);

// 아래 고정 입력 — 실패는 칸 위에 문구, 입장이 풀렸거나 링크·일지가 막히면 화면을 다시 읽어 그 상태를 그린다.
const CommentForm = ({ token, logId, max, onSent }: { token: string; logId: string; max: number; onSent: () => void }) => {
  const router = useRouter();
  const [body, setBody] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const length = lengthOf(body);
  const blocked = pending || body.trim() === "" || length > max;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (blocked) return;
    setPending(true);
    setError(null);
    try {
      const result = await addLogCommentByLink({ token, log_id: logId, body });
      if (!result.ok) {
        setError(result.message);
        if (result.reverify || result.locked || result.rejected) router.refresh();
        return;
      }
      setBody("");
      router.refresh();
      onSent();
    } catch (e) {
      if (settleRedirect(e)) return;
      setError(FAILED);
    } finally {
      setPending(false);
    }
  };

  return (
    <form
      className="pb-safe-2 fixed inset-x-0 bottom-0 z-10 border-t border-border bg-card px-3 pt-2"
      data-testid="customer-log-comment-form"
      onSubmit={submit}>
      <div className="mx-auto flex w-full max-w-md flex-col gap-y-1">
        {error && (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        )}
        <div className="flex items-center gap-x-2">
          <Input
            aria-label="댓글"
            className="min-h-10 min-w-0 flex-1"
            name="log-comment"
            variant="outline"
            placeholder="궁금한 점을 남겨 주세요"
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          <Button disabled={blocked} type="submit" variant="primary">
            남기기
          </Button>
        </div>
        {length > max && (
          <p className="text-xs text-destructive">
            {length}/{max}자 — 줄여 주세요
          </p>
        )}
      </div>
    </form>
  );
};

// 고객 시공일지 한 건 — 「내 현장」 과 같은 입장(링크 + 입장 쿠키) · 댓글은 아래 고정 칸에서 남긴다.
const Page = ({ token, data }: Props) => {
  const [sheet, setSheet] = useState(false);
  if (data.kind === "dead") return <UnusableLink />;
  if (data.kind === "gate") {
    return (
      <CustomerScreenShell>
        <ShareGate docName="시공일지" orgName={data.orgName} token={token} verify={verifyContractShare} />
      </CustomerScreenShell>
    );
  }
  if (data.kind === "notice") return <Notice text={data.text} />;
  if (data.kind === "missing") {
    return (
      <CustomerScreenShell className="p-0">
        <Header title="시공일지" token={token} />
        <p className="mx-auto max-w-md px-4 pt-6 text-sm text-muted-foreground" data-testid="customer-log-missing">
          볼 수 없는 시공일지입니다. 공사 첫 화면에서 다시 골라 주세요.
        </p>
      </CustomerScreenShell>
    );
  }

  const { orgName, log, photos, comments, maxCommentBody } = data;
  const meta = [log.author_name ?? orgName, log.weather, log.phase_name].filter((v): v is string => Boolean(v)).join(" · ");
  const afterSend = () => {
    // 첫 댓글이 아니어도 아직 안 본 사람에게만 — 앱 안·저장소 막힘이면 안 띄운다.
    if (!shouldInvite(APP_SHEET_KEY)) return;
    markSeen(browserSeenStore(), APP_SHEET_KEY);
    setSheet(true);
  };

  return (
    <CustomerScreenShell className="p-0 pb-28">
      <Header title="시공일지" token={token} />
      <div className="mx-auto flex w-full max-w-md flex-col gap-y-4 px-4 pt-4">
        <SectionCard as="article" className="flex flex-col gap-y-2" data-testid="customer-log-article">
          <div className="flex flex-col gap-y-0.5">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-base font-semibold tabular-nums">{formatDate(log.log_date)}</p>
              <Pill tone={LOG_KIND_TONE[log.kind]}>{PROJECT_LOG_KIND_LABEL[log.kind]}</Pill>
            </div>
            <p className="text-sm text-muted-foreground" data-testid="customer-log-meta">
              {meta}
            </p>
          </div>
          <p className="whitespace-pre-wrap break-words text-base">{log.body}</p>
          {photos.length > 0 && <LogPhotos photos={photos} />}
        </SectionCard>

        <section className="flex flex-col gap-y-2" data-testid="customer-log-comments">
          <h2 className="text-base font-semibold">댓글 {comments.length}</h2>
          {comments.length === 0 ? (
            <p className="text-sm text-muted-foreground">궁금한 점을 남겨 주시면 담당자가 답해 드립니다.</p>
          ) : (
            <ol className="flex flex-col gap-y-2">
              {comments.map((c) => {
                const who = commentAuthorOf(c, orgName);
                const mine = c.author_kind === "CUSTOMER";
                return (
                  <li key={c.id} className="flex gap-x-2.5">
                    <span
                      aria-hidden
                      className={cn(
                        "flex size-10 shrink-0 items-center justify-center rounded-full text-base font-semibold",
                        mine ? "bg-muted text-muted-foreground" : "bg-accent text-accent-foreground"
                      )}>
                      {Array.from(who)[0]}
                    </span>
                    <SectionCard as="div" className="flex min-w-0 flex-1 flex-col gap-y-1 text-sm" padding="sm">
                      <p className="flex items-center justify-between gap-2">
                        <span className="font-medium" data-testid="customer-log-comment-author">
                          {who}
                        </span>
                        <span className="text-xs tabular-nums text-muted-foreground">{formatDateTime(c.created_at)}</span>
                      </p>
                      <p className="whitespace-pre-wrap break-words">{c.body}</p>
                    </SectionCard>
                  </li>
                );
              })}
            </ol>
          )}
        </section>
      </div>
      <CommentForm logId={log.id} max={maxCommentBody} token={token} onSent={afterSend} />
      <AppSheet open={sheet} onClose={() => setSheet(false)} />
    </CustomerScreenShell>
  );
};

export default Page;
